import { BellIcon, CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  clearReadNotifications,
  deleteNotification,
  getNotifications,
  getNotificationUnreadCount,
  INotification,
  markAllNotificationsRead,
  markNotificationsRead,
} from 'services'
import { timeAgo } from 'utils'

import { getLevelClass, getNotificationLink, getNotificationMeta } from './notificationMeta'
import { onNotificationsChanged } from './notificationSync'
import { startToastCapture } from './toastCapture'

/** How often the badge re-checks. Polled rather than pushed: nginx blocks the WebSocket upgrade. */
const UnreadPollInterval = 45 * 1000
const PageSize = 30

/** Widest the panel gets (the sm: step below), plus the gap to keep from the viewport edge. */
const PanelWidthPx = 416
const PanelViewportMarginPx = 16

/** Two-line clamp, done inline: every `line-clamp-*` class is a no-op in this project's Tailwind. */
const clampTwoLines: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

/**
 * The header bell: an unread count, and a panel listing everything the user has been notified of -
 * new tasks, new loans, new messages, status changes, and the transient toasts the app used to show
 * for a few seconds and then lose.
 */
export const NotificationBell = () => {
  const history = useHistory()
  const containerRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<INotification[]>([])
  const [unread, setUnread] = useState(0)
  const [total, setTotal] = useState(0)
  const [unreadOnly, setUnreadOnly] = useState(false)
  // The bell sits at a different x in each header layout (pipeline vs a loan screen), so the panel
  // is flipped to hang off the right edge whenever opening leftward would run it off-viewport.
  const [alignRight, setAlignRight] = useState(false)

  // Capture toasts for as long as the header is mounted, which is every authenticated screen. The
  // subscription is module-level and deliberately not torn down on unmount: it is guarded against
  // double-subscribing, and a remount mid-session must not drop messages.
  useEffect(() => {
    startToastCapture()
  }, [])

  const loadUnreadCount = () => {
    getNotificationUnreadCount()
      .then((res) => setUnread(Number(res?.unread) || 0))
      .catch(() => {})
  }

  const loadList = () => {
    setLoading(true)
    getNotifications({ count: PageSize, unreadOnly: unreadOnly ? 'true' : 'false' })
      .then((res) => {
        setItems(res?.data || [])
        setTotal(Number(res?.total) || 0)
        setUnread(Number(res?.unread) || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  // The badge polls on its own; the list is only fetched when the panel is actually open.
  useEffect(() => {
    loadUnreadCount()
    const timer = setInterval(loadUnreadCount, UnreadPollInterval)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (open) loadList()
  }, [open, unreadOnly])

  // Measured on open rather than on a breakpoint: it is the button's own position that decides.
  useEffect(() => {
    if (!open) return
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setAlignRight(rect.left + PanelWidthPx > window.innerWidth - PanelViewportMarginPx)
  }, [open])

  // A toast logged while the panel is open should appear without reopening it.
  useEffect(() => {
    const off = onNotificationsChanged(() => {
      if (open) loadList()
      else loadUnreadCount()
    })
    return () => {
      off()
    }
  }, [open, unreadOnly])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const onOpenItem = (item: INotification) => {
    const link = getNotificationLink(item)
    if (!item.isRead) {
      setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)))
      setUnread((prev) => Math.max(prev - 1, 0))
      markNotificationsRead([item.id]).catch(() => {})
    }
    if (link) {
      setOpen(false)
      history.push(link)
    }
  }

  const onMarkAllRead = () => {
    setItems((prev) => prev.map((row) => ({ ...row, isRead: true })))
    setUnread(0)
    markAllNotificationsRead()
      .then(() => {
        // The unread-only tab is empty by definition once everything is read.
        if (unreadOnly) loadList()
      })
      .catch(() => loadList())
  }

  const onClearRead = () => {
    clearReadNotifications()
      .then(() => loadList())
      .catch(() => {})
  }

  const onDismiss = (e: React.MouseEvent, item: INotification) => {
    e.stopPropagation()
    setItems((prev) => prev.filter((row) => row.id !== item.id))
    if (!item.isRead) setUnread((prev) => Math.max(prev - 1, 0))
    deleteNotification(item.id)
      .then(() => setTotal((prev) => Math.max(prev - 1, 0)))
      .catch(() => loadList())
  }

  const badge = useMemo(() => (unread > 99 ? '99+' : `${unread}`), [unread])

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        type="button"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((prev) => !prev)}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors focus:outline-none ${
          open ? 'bg-sky-50 text-sky-700 dark:bg-sky-400/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100'
        }`}
      >
        <BellIcon className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.05rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-4 text-white">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-2 w-[22rem] overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl dark:border-slate-700 sm:w-[26rem] ${
            alignRight ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">Notifications</span>
              {unread > 0 && (
                <span className="inline-flex rounded-full bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-600">
                  {badge} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setUnreadOnly((prev) => !prev)}
                className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                  unreadOnly ? 'bg-sky-50 text-sky-700 dark:bg-sky-400/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {unreadOnly ? 'Unread' : 'All'}
              </button>
              <button
                type="button"
                title="Mark all as read"
                onClick={onMarkAllRead}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Clear the ones already read"
                onClick={onClearRead}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {loading && !items.length && (
              <div className="px-3 py-8 text-center text-xs text-gray-400">Loading…</div>
            )}
            {!loading && !items.length && (
              <div className="px-3 py-8 text-center text-xs text-gray-400">
                {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
              </div>
            )}
            {items.map((item) => {
              const { Icon, label } = getNotificationMeta(item.type)
              const link = getNotificationLink(item)
              return (
                <div
                  key={item.id}
                  onClick={() => onOpenItem(item)}
                  className={`group flex items-start gap-2.5 border-b border-gray-50 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-slate-700 ${
                    link ? 'cursor-pointer' : 'cursor-default'
                  } ${item.isRead ? '' : 'bg-sky-50/50 dark:bg-sky-400/10'}`}
                >
                  <div
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${getLevelClass(
                      item.level,
                    )}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1.5">
                      <p
                        className={`min-w-0 flex-1 text-[13px] leading-snug ${
                          item.isRead ? 'text-gray-700' : 'font-semibold text-gray-900'
                        }`}
                        style={clampTwoLines}
                      >
                        {item.title}
                      </p>
                      {!item.isRead && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />}
                      <button
                        type="button"
                        title="Dismiss"
                        onClick={(e) => onDismiss(e, item)}
                        className="shrink-0 rounded p-0.5 text-gray-300 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-600 group-hover:opacity-100"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {item.message && (
                      <p className="mt-0.5 text-[12px] leading-snug text-gray-500" style={clampTwoLines}>
                        {item.message}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-400">
                      <span>{label}</span>
                      {!!item.loanNumber && (
                        <>
                          <span>·</span>
                          <span>Loan #{item.loanNumber}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{timeAgo(new Date(item.createdAt))}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {total > items.length && (
            <div className="border-t border-gray-100 px-3 py-2 text-center text-[11px] text-gray-400 dark:border-slate-700">
              Showing the latest {items.length} of {total}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
